import React, { useState } from 'react';
import { Box, TextField, Button, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLogin, useNotify } from 'react-admin';
import { useSafeBrand } from './hooks/useSafeBrand';
import { BrandLogo } from './components/BrandLogo';
import { isGolfOSVariant } from './config/branding';
import { GolfOSLoginPage } from './components/golfos/GolfOSLoginPage';

const CustomLoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  // Use fallback branding for login page since it's outside tenant context
  const { brand } = useSafeBrand();

  // Default values while branding loads
  const buttonGradient =
    brand?.theme?.buttonGradient || 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login({ email, password }).catch(() => notify('Invalid email or password'));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 400,
      }}
    >
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        fullWidth
      />
      <TextField
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                aria-label="toggle password visibility"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{
          mt: 2,
          background: buttonGradient,
          '&:hover': {
            background: buttonGradient,
            opacity: 0.9,
          },
        }}
      >
        Sign In
      </Button>
    </Box>
  );
};

export const LoginPage = () => {
  if (isGolfOSVariant) {
    return <GolfOSLoginPage />;
  }

  // Use fallback branding for login page since it's outside tenant context
  const { brand } = useSafeBrand();

  const headerGradient =
    brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        background: headerGradient,
        paddingTop: '15vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '48px 40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: 400,
        }}
      >
        <BrandLogo
          width={200}
          height={40}
          sx={{
            marginBottom: '40px',
          }}
        />
        <CustomLoginForm />
      </Box>
    </Box>
  );
};
